import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AndroidIcon from '@mui/icons-material/Android';
import LaptopIcon from '@mui/icons-material/Laptop';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';

interface InstallAppMenuItemProps {
  onClose?: () => void;
  className?: string;
}

const InstallAppMenuItem: React.FC<InstallAppMenuItemProps> = ({ onClose, className }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [deviceType, setDeviceType] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone) {
      setIsInstalled(true);
      return;
    }

    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/.test(userAgent);
    
    if (isIOS) {
      setDeviceType('ios');
    } else if (isAndroid) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Listen for Chrome/Edge install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted PWA install');
        setIsInstalled(true);
        setShowDialog(false);
      }
      
      setDeferredPrompt(null);
    } else {
      // Show manual instructions
      setShowDialog(true);
    }
  };

  const handleMenuClick = () => {
    if (isInstalled) {
      // Already installed, maybe show a message
      return;
    }
    
    if (deferredPrompt && deviceType !== 'ios') {
      // Can install directly
      handleInstallClick();
    } else {
      // Show instructions
      setShowDialog(true);
    }
    
    // Close the menu if provided
    onClose?.();
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  const getInstructions = () => {
    switch (deviceType) {
      case 'ios':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              iOS requires manual installation through Safari
            </Alert>
            <Stepper orientation="vertical" activeStep={-1}>
              <Step expanded>
                <StepLabel>Open this website in Safari</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Note: Installation only works in Safari, not Chrome or other browsers on iOS
                  </Typography>
                </StepContent>
              </Step>
              <Step expanded>
                <StepLabel>
                  Tap the Share button <ShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    It's the square with an arrow pointing up, usually at the bottom of the screen
                  </Typography>
                </StepContent>
              </Step>
              <Step expanded>
                <StepLabel>
                  Scroll down and tap "Add to Home Screen" <AddIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    You might need to scroll down in the share menu to find this option
                  </Typography>
                </StepContent>
              </Step>
              <Step expanded>
                <StepLabel>Name it "SOG" and tap "Add"</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    The app icon will appear on your home screen
                  </Typography>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        );
      
      case 'android':
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Android supports automatic installation
            </Alert>
            <Stepper orientation="vertical" activeStep={-1}>
              <Step expanded>
                <StepLabel>Option 1: Automatic Installation</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Chrome will show an "Add to Home Screen" prompt automatically after browsing for a bit
                  </Typography>
                </StepContent>
              </Step>
              <Step expanded>
                <StepLabel>Option 2: Manual Installation</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" component="div">
                    1. Tap the menu button (3 dots) in Chrome<br/>
                    2. Select "Install app" or "Add to Home Screen"<br/>
                    3. Confirm the installation
                  </Typography>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        );
      
      default: // desktop
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Desktop Chrome/Edge supports app installation
            </Alert>
            <Stepper orientation="vertical" activeStep={-1}>
              <Step expanded>
                <StepLabel>Look for the install icon in the address bar</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    A small install icon (+) appears on the right side of the address bar
                  </Typography>
                </StepContent>
              </Step>
              <Step expanded>
                <StepLabel>Or use the browser menu</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" component="div">
                    Chrome: Menu (3 dots) → "Install SOG Restaurant POS"<br/>
                    Edge: Menu (3 dots) → Apps → "Install this site as an app"
                  </Typography>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        );
    }
  };

  return (
    <>
      <button
        onClick={handleMenuClick}
        className={className || "nav-button"}
        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        <GetAppIcon style={{ fontSize: 20 }} />
        <span>Install App</span>
      </button>

      <Dialog 
        open={showDialog} 
        onClose={() => setShowDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {deviceType === 'ios' && <PhoneIphoneIcon />}
          {deviceType === 'android' && <AndroidIcon />}
          {deviceType === 'desktop' && <LaptopIcon />}
          Install SOG App
        </DialogTitle>
        <DialogContent>
          {getInstructions()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Close</Button>
          {deferredPrompt && deviceType !== 'ios' && (
            <Button 
              variant="contained" 
              onClick={handleInstallClick}
              startIcon={<GetAppIcon />}
            >
              Install Now
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallAppMenuItem;